import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { trackArticleOfferClick } from '../../services/offerClickService';

const Section = styled.aside`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 0.75rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(props) => props.theme.colors.mutedForeground};
  margin: 0 0 0.75rem;
`;

const SponsoredNote = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.6875rem;
  color: ${(props) => props.theme.colors.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ScrollArea = styled.div`
  max-height: 26.25rem;
  overflow-y: auto;
  padding-right: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.border};
    border-radius: 999px;
  }
`;

const OfferCard = styled.article`
  display: grid;
  grid-template-columns: 3.5rem minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.xl};
  background: ${(props) => props.theme.colors.card};
  box-shadow: ${(props) => props.theme.shadows.small};
`;

const OfferImage = styled.img`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  object-fit: cover;
  background: ${(props) => props.theme.colors.muted};
`;

const ImagePlaceholder = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  background: ${(props) => props.theme.colors.muted};
`;

const OfferBody = styled.div`
  min-width: 0;
`;

const OfferTitle = styled.h3`
  margin: 0 0 0.2rem;
  font-size: 0.875rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  line-height: 1.3;
  color: ${(props) => props.theme.colors.foreground};
`;

const OfferDescription = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${(props) => props.theme.colors.mutedForeground};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OfferButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.colors.border};
  background: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.primaryForeground || '#fff'};
  text-decoration: none;
  flex-shrink: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) => props.theme.shadows.medium};
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

function isDisplayableOffer(offer) {
  return Boolean(offer?.title?.trim() && offer?.url?.trim());
}

export default function ArticleOffers({ offers = [], articleId, language = 'en' }) {
  const { t } = useTranslation();
  const visibleOffers = offers.filter(isDisplayableOffer);

  if (!visibleOffers.length) return null;

  const handleOfferClick = (offer) => {
    trackArticleOfferClick({
      articleId,
      offerId: offer._id,
      language,
      url: offer.url,
      title: offer.title,
    });
  };

  return (
    <Section aria-label={t('article.offers', 'Offers')}>
      <SectionTitle>{t('article.offers', 'Offers')}</SectionTitle>
      <SponsoredNote>{t('article.sponsoredOffers', 'Sponsored')}</SponsoredNote>
      <ScrollArea>
        {visibleOffers.map((offer, index) => (
          <OfferCard
            key={offer._id || `${offer.url}-${index}`}
            data-ad="article-offer"
            data-ad-placement="article-sidebar"
          >
            {offer.imageUrl?.trim() ? (
              <OfferImage src={offer.imageUrl} alt="" loading="lazy" />
            ) : (
              <ImagePlaceholder aria-hidden="true" />
            )}
            <OfferBody>
              <OfferTitle>{offer.title}</OfferTitle>
              {offer.description?.trim() ? (
                <OfferDescription>{offer.description}</OfferDescription>
              ) : null}
            </OfferBody>
            <OfferButton
              href={offer.url}
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              data-ad="article-offer-link"
              data-ad-placement="article-sidebar"
              aria-label={offer.buttonLabel || t('article.viewOffer', 'View offer')}
              title={offer.buttonLabel || t('article.viewOffer', 'View offer')}
              onClick={() => handleOfferClick(offer)}
            >
              <ExternalLink aria-hidden="true" />
            </OfferButton>
          </OfferCard>
        ))}
      </ScrollArea>
    </Section>
  );
}
